import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

interface CheckoutVars {
  period: "monthly" | "annual";
  promo?: string;
}

/**
 * Whether self-serve billing is live. False during the beta (Stripe keys
 * unset → backend returns enabled:false), which the UI uses to hide upgrade
 * CTAs instead of letting a click hit a 503. Flips true automatically at
 * cutover, no frontend rebuild needed.
 */
export function useBillingConfig() {
  return useQuery({
    queryKey: ["billing", "config"],
    queryFn: () =>
      apiClient.get("api/billing/config").json<{ enabled: boolean }>(),
    staleTime: 5 * 60_000,
  });
}

/** Create a Stripe Checkout Session and redirect to it. */
export function useBillingCheckout() {
  return useMutation({
    mutationFn: (vars: CheckoutVars) =>
      apiClient
        .post("api/billing/checkout", { json: vars, timeout: 120_000 })
        .json<{ url: string }>(),
    // onSuccess does a redirect, not a cache write — safe (CLAUDE.md #8 only
    // bars onSuccess for cache writes).
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

/** Create a Stripe Customer Portal session and redirect to it. */
export function useBillingPortal() {
  return useMutation({
    mutationFn: () =>
      apiClient.post("api/billing/portal", { timeout: 120_000 }).json<{ url: string }>(),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}
